import type LoginDTO from '@trading/core/src/repositories/auth/dto/login.dto'
import type RegisterDTO from '@trading/core/src/repositories/auth/dto/register.dto'
import AuthService from '@trading/core/src/services/auth.service'
import type { SsoDTO } from '@trading/core'
import { AppError, NetworkError, NotFoundError, ValidationError } from '@trading/infrastructure'
import { decodeJwtPayload, getJwtSessionScope } from '~/utils/jwt'
import { api } from '~/services/composition'

export const useAuthStore = defineStore(
  'auth',
  () => {
    const accessToken = ref<string | null>('')
    const sessionScope = computed(() => getJwtSessionScope(accessToken.value))
    const isLoading = ref(false)
    const refreshTokenRequest = ref<Promise<string | null> | null>(null)
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    const logoutRequest = ref<Promise<void | null> | null>(null)
    const authService = new AuthService(api.auth)
    const toast = useToast()
    const router = useRouter()
    const { t } = useI18n()
    const { reset, setIsAuthenticated } = useUserStore()
    const featureStore = useFeatureStore()
    const { features } = storeToRefs(featureStore)
    const { refetch: featuresRefetch } = featureStore

    function clearAuth() {
      accessToken.value = null
      localStorage.removeItem('auth')
    }

    function establishSession(token: string) {
      accessToken.value = token
      setIsAuthenticated(true)
    }

    async function register(params: RegisterDTO) {
      isLoading.value = true
      try {
        authService.register(params)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_e) {
        // TODO handle error
      } finally {
        isLoading.value = false
      }
    }

    async function login(params: LoginDTO) {
      isLoading.value = true
      const nuxtApp = useNuxtApp()

      try {
        const isAccountLogin = features.value?.login_type === 'account'
        const resp = isAccountLogin
          ? await authService.loginByAccount(params)
          : await authService.login(params)
        toast.add({
          title: String(t('common.success')),
          description: String(t('common.welcomeMsg')),
          color: 'success'
        })

        if (resp) {
          establishSession(resp.access_token)
          await nuxtApp.callHook('auth:login')
          router.push('/')
        } else {
          toast.add({
            title: String(t('login.error.title')),
            description: String(t('login.error.BAD_RESPONSE')),
            color: 'error'
          })
          throw new Error(String(t('login.error.BAD_RESPONSE')))
        }
      } catch (e: unknown) {
        clearAuth()
        reset()

        // нужно пробросить ошибку, иначе вызывающему коду придется скрывать лоадер что будет приводить
        // к морганию лоадера при успешной авторизации (мы сначала его скроем и пользователь
        // увидит компонент логина, потом при монтировании dockview опять покажем)
        // скрываем лоадер тольrо при ошибке, при успехе он продолжает висеть
        if (e instanceof AppError) {
          const code = e.code
          const message = String(t('login.error.' + code || 'errors.' + code)) || e.message
          const title = String(t('login.error.title')) || e.message

          if (e instanceof NotFoundError) {
            toast.add({ title: title, description: message, color: 'error' })
            throw e
          }

          if (e instanceof ValidationError) {
            toast.add({ title: title, description: message, color: 'error' })
            throw e
          }

          if (e instanceof NetworkError) {
            toast.add({ title: title, description: message, color: 'error' })
            throw e
          }
        }
        toast.add({
          title: String(t('login.error.title')),
          description: String(
            t('login.error.UNKNOWN', { message: e instanceof Error ? e.message : '' })
          ),
          color: 'error'
        })
        throw e
      } finally {
        isLoading.value = false
      }
    }

    async function loginBySso(params: SsoDTO) {
      isLoading.value = true
      const nuxtApp = useNuxtApp()
      clearAuth()
      await nuxtApp.callHook('auth:logout')
      await nextTick()

      try {
        const resp = await authService.loginBySso(params)
        toast.add({
          title: String(t('common.success')),
          description: String(t('common.welcomeMsg')),
          color: 'success'
        })

        if (resp) {
          establishSession(resp.access_token)
          await nuxtApp.callHook('auth:login')
          await nextTick()
        } else {
          toast.add({
            title: String(t('login.error.title')),
            description: String(t('login.error.BAD_RESPONSE')),
            color: 'error'
          })
          throw new Error(String(t('login.error.BAD_RESPONSE')))
        }
      } catch (e: unknown) {
        // нужно пробросить ошибку, иначе вызывающему коду придется скрывать лоадер что будет приводить
        // к морганию лоадера при успешной авторизации (мы сначала его скроем и пользователь
        // увидит компонент логина, потом при монтировании dockview опять покажем)
        // скрываем лоадер тольrо при ошибке, при успехе он продолжает висеть
        if (e instanceof AppError) {
          const code = e.code
          const message = String(t('login.error.' + code || 'errors.' + code)) || e.message
          const title = String(t('login.error.title')) || e.message

          if (e instanceof NotFoundError) {
            toast.add({ title: title, description: message, color: 'error' })
            throw e
          }

          if (e instanceof ValidationError) {
            toast.add({ title: title, description: message, color: 'error' })
            throw e
          }

          if (e instanceof NetworkError) {
            toast.add({ title: title, description: message, color: 'error' })
            throw e
          }
        }
        toast.add({
          title: String(t('login.error.title')),
          description: String(
            t('login.error.UNKNOWN', { message: e instanceof Error ? e.message : '' })
          ),
          color: 'error'
        })
        throw e
      } finally {
        isLoading.value = false
      }
    }

    async function logout() {
      if (logoutRequest.value) return
      isLoading.value = true
      const nuxtApp = useNuxtApp()

      try {
        await featuresRefetch()
        // Нет необходимости делать на бэкенд вызов логаута,
        // тем более там нет такого ендпоинта и не планируется
        return
        // logoutRequest.value = authService.logout()
        // await logoutRequest.value
      } catch (e: unknown) {
        if (e instanceof AppError) {
          const code = e.code
          const message = String(t('logout.error.' + code || 'errors.' + code)) || e.message
          const title = String(t('logout.error.title')) || e.message

          if (e instanceof NotFoundError) {
            toast.add({ title: title, description: message, color: 'error' })
            return Promise.resolve(null)
          }

          if (e instanceof ValidationError) {
            toast.add({ title: title, description: message, color: 'error' })
            return Promise.resolve(null)
          }

          if (e instanceof NetworkError) {
            toast.add({ title: title, description: message, color: 'error' })
            return Promise.resolve(null)
          }

          toast.add({ title: title, description: message, color: 'error' })
          return Promise.resolve(null)
        } else {
          toast.add({
            title: String(t('logout.error.title')),
            description: String(
              t('logout.error.UNKNOWN', { message: e instanceof Error ? e.message : '' })
            ),
            color: 'error'
          })
        }
      } finally {
        isLoading.value = false
        logoutRequest.value = null

        clearAuth()
        await nuxtApp.callHook('auth:logout')
        // Сомнительно чистить локал сторадж, у нас там могут быть настройки пользователя
        // localStorage.clear()
        router.push('/auth')
      }
    }

    /** Для configureApiClient: отдаёт текущий access token. */
    async function getToken(): Promise<string | null> {
      if (!accessToken.value) return null

      const expDateStr = decodeJwtPayload(accessToken.value)?.exp
      const expDate = new Date(0)
      expDate.setUTCSeconds(parseInt(String(expDateStr), 10))

      if (expDate.getTime() < new Date().getTime() + 12000) {
        if (refreshTokenRequest.value === null) {
          refreshTokenRequest.value = refreshSession()
        }

        try {
          accessToken.value = await refreshTokenRequest.value
        } finally {
          refreshTokenRequest.value = null
        }
      }

      return accessToken.value
    }

    /**
     * Обновление сессии по refresh (cookie отправляется с запросом при withCredentials).
     * Для httpOnly cookie в body передаём пустое значение; бэкенд читает из cookie.
     */
    async function refreshSession() {
      try {
        const response = await authService.refresh()
        const newAccessToken = response?.access_token
        if (!newAccessToken) {
          toast.add({
            title: String(t('refresh.error.title')),
            description: String(t('refresh.error.NO_ACCESS_TOKEN')),
            color: 'error'
          })
          return null
        }

        accessToken.value = newAccessToken
        setIsAuthenticated(true)
        return newAccessToken
        // const account = await userService.getCurrentUser();
        // if (account) {
        //   accessToken.value = response.access_token
        // }
      } catch (e) {
        if (e instanceof ValidationError) {
          toast.add({
            title: String(t('refresh.error.title')),
            description: String(t('refresh.error.INVALID_TOKEN')),
            color: 'error'
          })
          clearAuth()
          router.push('/auth')
          throw e
        }
        toast.add({
          title: String(t('refresh.error.title')),
          description: String(t('refresh.error.UNKNOWN')),
          color: 'error'
        })
        await logout()
        router.push('/auth')
        accessToken.value = null
        return null
      }
    }
    return {
      accessToken,
      sessionScope,
      isLoading,
      clearAuth,
      getToken,
      refreshSession,
      login,
      loginBySso,
      logout,
      refreshTokenRequest,
      register
    }
  },
  {
    persist: true
  }
)
